import { BitBuffer } from './BitBuffer.js';
import { ErrorCorrectLevel } from './ErrorCorrectLevel.js';
import { Polynomial } from './Polynomial.js';
import { QR8BitByte } from './QR8BitByte.js';
import { QRAlphaNum } from './QRAlphaNum.js';
import { QRData } from './QRData.js';
import { QRKanji } from './QRKanji.js';
import { QRNumber } from './QRNumber.js';
import { QRUtil } from './QRUtil.js';
import { RSBlock } from './RSBlock.js';
// eslint-disable-next-line @typescript-eslint/no-redeclare
const TypeNumber = Array(41)
    .fill(0)
    .map((_, i) => i);
export { TypeNumber };
export var ErrorCorrectionLevel;
(function (ErrorCorrectionLevel) {
    ErrorCorrectionLevel["L"] = "L";
    ErrorCorrectionLevel["M"] = "M";
    ErrorCorrectionLevel["Q"] = "Q";
    ErrorCorrectionLevel["H"] = "H";
})(ErrorCorrectionLevel || (ErrorCorrectionLevel = {}));
export var Mode;
(function (Mode) {
    Mode["numeric"] = "numeric";
    Mode["alphanumeric"] = "alphanumeric";
    /**
     * Default
     */
    Mode["byte"] = "byte";
    Mode["kanji"] = "kanji";
    Mode["unicode"] = "unicode";
})(Mode || (Mode = {}));
/**
 * QRCode
 *
 * @author Kazuhiko Arase
 */
export class QRCodeMinimal {
    static stringToBytesFuncs = {
        default(s) {
            const bytes = [];
            for (let i = 0; i < s.length; i += 1) {
                const c = s.charCodeAt(i);
                bytes.push(c & 0xff);
            }
            return bytes;
        }
    };
    static PAD0 = 0xec;
    static PAD1 = 0x11;
    typeNumber;
    errorCorrectLevel;
    qrDataList;
    modules = [];
    moduleCount = 0;
    constructor(typeNumber = 0, errorCorrectionLevel = ErrorCorrectionLevel.L) {
        this.typeNumber = typeNumber;
        this.errorCorrectLevel = ErrorCorrectLevel[errorCorrectionLevel];
        this.qrDataList = [];
    }
    getTypeNumber() {
        return this.typeNumber;
    }
    setTypeNumber(typeNumber) {
        this.typeNumber = typeNumber;
    }
    getErrorCorrectLevel() {
        return this.errorCorrectLevel;
    }
    setErrorCorrectLevel(errorCorrectLevel) {
        this.errorCorrectLevel = errorCorrectLevel;
    }
    clearData() {
        this.qrDataList = [];
    }
    addData(qrData, mode = Mode.byte) {
        if (qrData instanceof QRData) {
            this.qrDataList.push(qrData);
        }
        else if (typeof qrData === 'string') {
            switch (mode) {
                case Mode.numeric:
                    this.qrDataList.push(new QRNumber(qrData));
                    break;
                case Mode.alphanumeric:
                    this.qrDataList.push(new QRAlphaNum(qrData));
                    break;
                case Mode.byte:
                    this.qrDataList.push(new QR8BitByte(qrData, QRCodeMinimal.stringToBytesFuncs.default));
                    break;
                case Mode.unicode:
                    this.qrDataList.push(new QR8BitByte(qrData, QRCodeMinimal.stringToBytesFuncs['UTF8']));
                    break;
                case Mode.kanji:
                    this.qrDataList.push(new QRKanji(qrData, QRCodeMinimal.stringToBytesFuncs['SJIS']));
                    break;
                default:
                    throw new Error('mode:' + mode);
            }
        }
        else {
            throw new Error(typeof qrData);
        }
    }
    /*private getDataCount(): number {
        return this.qrDataList.length
    }

    private getData(index: number): QRData {
        return this.qrDataList[index]
    }*/
    isDark(row, col) {
        if (this.modules[row][col] != null) {
            return this.modules[row][col];
        }
        else {
            return false;
        }
    }
    getModuleCount() {
        return this.moduleCount;
    }
    make() {
        if (this.typeNumber < 1) {
            let typeNumber = 1;
            for (; typeNumber < 40; typeNumber++) {
                const rsBlocks = RSBlock.getRSBlocks(typeNumber, this.errorCorrectLevel);
                const buffer = new BitBuffer();
                this.qrDataList.forEach(data => {
                    buffer.put(data.getMode(), 4);
                    buffer.put(data.getLength(), data.getLengthInBits(typeNumber));
                    data.write(buffer);
                });
                const totalDataCount = rsBlocks.reduce((t, v) => t + v.getDataCount(), 0);
                if (buffer.getLengthInBits() <= totalDataCount * 8) {
                    break;
                }
            }
            this.typeNumber = typeNumber;
        }
        this.makeImpl(false, this.getBestMaskPattern());
    }
    getBestMaskPattern() {
        let minLostPoint = 0;
        let pattern = 0;
        for (let i = 0; i < 8; i += 1) {
            this.makeImpl(true, i);
            const lostPoint = QRUtil.getLostPoint(this);
            if (i == 0 || minLostPoint > lostPoint) {
                minLostPoint = lostPoint;
                pattern = i;
            }
        }
        return pattern;
    }
    makeImpl(test, maskPattern) {
        // initialize modules
        this.moduleCount = this.typeNumber * 4 + 17;
        this.modules = [];
        for (let i = 0; i < this.moduleCount; i += 1) {
            this.modules.push([]);
            for (let j = 0; j < this.moduleCount; j += 1) {
                this.modules[i].push(null);
            }
        }
        this.setupPositionProbePattern(0, 0);
        this.setupPositionProbePattern(this.moduleCount - 7, 0);
        this.setupPositionProbePattern(0, this.moduleCount - 7);
        this.setupPositionAdjustPattern();
        this.setupTimingPattern();
        this.setupTypeInfo(test, maskPattern);
        if (this.typeNumber >= 7) {
            this.setupTypeNumber(test);
        }
        const data = QRCodeMinimal.createData(this.typeNumber, this.errorCorrectLevel, this.qrDataList);
        this.mapData(data, maskPattern);
    }
    mapData(data, maskPattern) {
        let inc = -1;
        let row = this.moduleCount - 1;
        let bitIndex = 7;
        let byteIndex = 0;
        const maskFunc = QRUtil.getMaskFunc(maskPattern);
        for (let col = this.moduleCount - 1; col > 0; col -= 2) {
            if (col == 6) {
                col -= 1;
            }
            while (true) {
                for (let c = 0; c < 2; c += 1) {
                    if (this.modules[row][col - c] == null) {
                        let dark = false;
                        if (byteIndex < data.length) {
                            dark = ((data[byteIndex] >>> bitIndex) & 1) == 1;
                        }
                        const mask = maskFunc(row, col - c);
                        if (mask) {
                            dark = !dark;
                        }
                        this.modules[row][col - c] = dark;
                        bitIndex -= 1;
                        if (bitIndex == -1) {
                            byteIndex += 1;
                            bitIndex = 7;
                        }
                    }
                }
                row += inc;
                if (row < 0 || this.moduleCount <= row) {
                    row -= inc;
                    inc = -inc;
                    break;
                }
            }
        }
    }
    setupPositionAdjustPattern() {
        const pos = QRUtil.getPatternPosition(this.typeNumber);
        pos.forEach(row => {
            pos.forEach(col => {
                if (this.modules[row][col] != null) {
                    return;
                }
                for (let r = -2; r <= 2; r += 1) {
                    for (let c = -2; c <= 2; c += 1) {
                        if (r == -2 || r == 2 || c == -2 || c == 2 || (r == 0 && c == 0)) {
                            this.modules[row + r][col + c] = true;
                        }
                        else {
                            this.modules[row + r][col + c] = false;
                        }
                    }
                }
            });
        });
    }
    setupPositionProbePattern(row, col) {
        for (let r = -1; r <= 7; r += 1) {
            for (let c = -1; c <= 7; c += 1) {
                if (row + r <= -1 || this.moduleCount <= row + r || col + c <= -1 || this.moduleCount <= col + c) {
                    continue;
                }
                if ((0 <= r && r <= 6 && (c == 0 || c == 6)) ||
                    (0 <= c && c <= 6 && (r == 0 || r == 6)) ||
                    (2 <= r && r <= 4 && 2 <= c && c <= 4)) {
                    this.modules[row + r][col + c] = true;
                }
                else {
                    this.modules[row + r][col + c] = false;
                }
            }
        }
    }
    setupTimingPattern() {
        for (let r = 8; r < this.moduleCount - 8; r += 1) {
            if (this.modules[r][6] != null) {
                continue;
            }
            this.modules[r][6] = r % 2 == 0;
        }
        for (let c = 8; c < this.moduleCount - 8; c += 1) {
            if (this.modules[6][c] != null) {
                continue;
            }
            this.modules[6][c] = c % 2 == 0;
        }
    }
    setupTypeNumber(test) {
        const bits = QRUtil.getBCHTypeNumber(this.typeNumber);
        for (let i = 0; i < 18; i += 1) {
            this.modules[~~(i / 3)][(i % 3) + this.moduleCount - 8 - 3] = !test && ((bits >> i) & 1) == 1;
        }
        for (let i = 0; i < 18; i += 1) {
            this.modules[(i % 3) + this.moduleCount - 8 - 3][~~(i / 3)] = !test && ((bits >> i) & 1) == 1;
        }
    }
    setupTypeInfo(test, maskPattern) {
        const data = (this.errorCorrectLevel << 3) | maskPattern;
        const bits = QRUtil.getBCHTypeInfo(data);
        // vertical
        for (let i = 0; i < 15; i += 1) {
            const mod = !test && ((bits >> i) & 1) == 1;
            if (i < 6) {
                this.modules[i][8] = mod;
            }
            else if (i < 8) {
                this.modules[i + 1][8] = mod;
            }
            else {
                this.modules[this.moduleCount - 15 + i][8] = mod;
            }
        }
        // horizontal
        for (let i = 0; i < 15; i += 1) {
            const mod = !test && ((bits >> i) & 1) == 1;
            if (i < 8) {
                this.modules[8][this.moduleCount - i - 1] = mod;
            }
            else if (i < 9) {
                this.modules[8][15 - i - 1 + 1] = mod;
            }
            else {
                this.modules[8][15 - i - 1] = mod;
            }
        }
        // fixed
        this.modules[this.moduleCount - 8][8] = !test;
    }
    static createData(typeNumber, errorCorrectLevel, dataArray) {
        const rsBlocks = RSBlock.getRSBlocks(typeNumber, errorCorrectLevel);
        const buffer = new BitBuffer();
        dataArray.forEach(data => {
            buffer.put(data.getMode(), 4);
            buffer.put(data.getLength(), data.getLengthInBits(typeNumber));
            data.write(buffer);
        });
        // calc max data count
        const totalDataCount = rsBlocks.reduce((t, v) => t + v.getDataCount(), 0);
        if (buffer.getLengthInBits() > totalDataCount * 8) {
            throw new Error('code length overflow. (' + buffer.getLengthInBits() + '>' + totalDataCount * 8 + ')');
        }
        // end
        if (buffer.getLengthInBits() + 4 <= totalDataCount * 8) {
            buffer.put(0, 4);
        }
        // padding
        while (buffer.getLengthInBits() % 8 != 0) {
            buffer.putBit(false);
        }
        // padding
        while (true) {
            if (buffer.getLengthInBits() >= totalDataCount * 8) {
                break;
            }
            buffer.put(QRCodeMinimal.PAD0, 8);
            if (buffer.getLengthInBits() >= totalDataCount * 8) {
                break;
            }
            buffer.put(QRCodeMinimal.PAD1, 8);
        }
        return QRCodeMinimal.createBytes(buffer, rsBlocks);
    }
    static createBytes(buffer, rsBlocks) {
        let offset = 0;
        let maxDcCount = 0;
        let maxEcCount = 0;
        const dcdata = new Array(rsBlocks.length).map(_ => []);
        const ecdata = new Array(rsBlocks.length).map(_ => []);
        function createNumArray(len) {
            const a = [];
            for (let i = 0; i < len; i += 1) {
                a.push(0);
            }
            return a;
        }
        for (let r = 0; r < rsBlocks.length; r += 1) {
            const dcCount = rsBlocks[r].getDataCount();
            const ecCount = rsBlocks[r].getTotalCount() - dcCount;
            maxDcCount = Math.max(maxDcCount, dcCount);
            maxEcCount = Math.max(maxEcCount, ecCount);
            dcdata[r] = createNumArray(dcCount);
            for (let i = 0; i < dcdata[r].length; i += 1) {
                dcdata[r][i] = 0xff & buffer.getBuffer()[i + offset];
            }
            offset += dcCount;
            const rsPoly = QRUtil.getErrorCorrectPolynomial(ecCount);
            const rawPoly = new Polynomial(dcdata[r], rsPoly.getLength() - 1);
            const modPoly = rawPoly.mod(rsPoly);
            ecdata[r] = createNumArray(rsPoly.getLength() - 1);
            for (let i = 0; i < ecdata[r].length; i += 1) {
                const modIndex = i + modPoly.getLength() - ecdata[r].length;
                ecdata[r][i] = modIndex >= 0 ? modPoly.getAt(modIndex) : 0;
            }
        }
        const totalCodeCount = rsBlocks.reduce((t, v) => t + v.getTotalCount(), 0);
        const data = createNumArray(totalCodeCount);
        let index = 0;
        for (let i = 0; i < maxDcCount; i += 1) {
            for (let r = 0; r < rsBlocks.length; r += 1) {
                if (i < dcdata[r].length) {
                    data[index] = dcdata[r][i];
                    index += 1;
                }
            }
        }
        for (let i = 0; i < maxEcCount; i += 1) {
            for (let r = 0; r < rsBlocks.length; r += 1) {
                if (i < ecdata[r].length) {
                    data[index] = ecdata[r][i];
                    index += 1;
                }
            }
        }
        return data;
    }
}
//# sourceMappingURL=QRCodeMinimal.js.map